import * as React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { PaywallScreens } from '~analytics/types';
import { translate } from '~l10n/client';

import { toast } from '~shared/components/Toast';
import { useRoomsPaywallDispatch } from '~teacher/components/rooms/RoomsPaywall/RoomsPaywallProvider';
import { roomsKeys } from '~teacher/hook/room/roomsKeys';
import { useMaxRosterSize } from '~teacher/hook/room/useMaxRosterSize';
import { queryClient } from '~teacher/queryClient';
import ArrowDown from 'ArrowDown';
import ImportRosterMessage from 'ImportRosterMessage';
import MenuButton from 'MenuButton';
import platform from 'Platform';
import StudentEntryView from 'StudentEntryView';

import { MAX_PRO_ROSTERED_STUDENTS } from '../../../../../shared/Constants';

const Constants = require('Constants');

const MAX_STUDENTS_ON_PAGE = 40;

let c = null;
let m = null;

class StudentsInRosterList extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      studentsList: this.props.studentsList,
    };
  }

  UNSAFE_componentWillReceiveProps(newProps) {
    this.setState({
      studentsList: newProps.studentsList,
    });
  }

  render() {
    let rosterChildren = [];

    if (this.state.studentsList) {
      for (let student of this.state.studentsList) {
        rosterChildren.push(
          <li key={student.id}>
            <StudentEntryView
              studentData={student}
              editMode={student.inEdit}
              newStudent={false}
              roomId={this.props.roomId}
              roomName={this.props.roomName}
              {...this.props}
            />
          </li>
        );
      }
    }

    let presenceFilter =
      m.studentFilter === 'presenceASC' || m.studentFilter === 'presenceDSC';
    let nameFilter =
      m.studentFilter === 'nameASC' || m.studentFilter === 'nameDSC';
    let studentIdFilter =
      m.studentFilter === 'student_idASC' ||
      m.studentFilter === 'student_idDSC';
    let emailFilter =
      m.studentFilter === 'emailASC' || m.studentFilter === 'emailDSC';

    return (
      <div
        style={{ display: this.props.hideRosterData ? 'none' : 'block' }}
        className="roster-wrapper"
      >
        <div className="roster-header clearfix">
          <div
            className={
              'roster-filter col in-room' + (presenceFilter ? ' active' : '')
            }
            onClick={() => c.filterStudents('presence')}
          >
            <span>{translate('Here', true)}</span>
            <span className="logged-in-students">
              {this.props.students_logged_in || 0}
            </span>
            <i data-icon={m.studentFilter === 'presenceASC' ? 'k' : 'm'} />
          </div>
          <span
            className={
              'roster-filter col student-name' + (nameFilter ? ' active' : '')
            }
            onClick={() => c.filterStudents('name')}
          >
            <span>{translate('Student Name')}</span>
            <i data-icon={m.studentFilter === 'nameASC' ? 'k' : 'm'} />
          </span>
          <span
            className={
              'roster-filter col student-id student-id-header' +
              (studentIdFilter ? ' active' : '')
            }
            onClick={() => c.filterStudents('student_id')}
          >
            <span>{translate('Student ID')}</span>
            <i data-icon={m.studentFilter === 'student_idASC' ? 'k' : 'm'} />
          </span>
          <span
            className={
              'roster-filter col email email-header' +
              (emailFilter ? ' active' : '')
            }
            onClick={() => c.filterStudents('email')}
          >
            <span>{translate('Email')}</span>
            <i data-icon={m.studentFilter === 'emailASC' ? 'k' : 'm'} />
          </span>
          <span className="student-action-spacer" />
        </div>
        <ul className="roster-list">{rosterChildren}</ul>
      </div>
    );
  }
}

function RosterActionButtons(props) {
  const queryClient = useQueryClient();
  const paywallDispatch = useRoomsPaywallDispatch();
  const maxStudentsInRoster = useMaxRosterSize();

  const { roomName } = props;
  let numberOfPages =
    Math.floor(props.numberOfStudents / MAX_STUDENTS_ON_PAGE) + 1;

  // If the total number of students is evenly divisible by the students on the page, we don't need the last page.
  if (props.numberOfStudents % MAX_STUDENTS_ON_PAGE === 0) {
    numberOfPages--;
  }

  let firstStudentOrder = (props.currentPage - 1) * MAX_STUDENTS_ON_PAGE + 1;
  let lastStudentOrder = Math.min(
    firstStudentOrder + (MAX_STUDENTS_ON_PAGE - 1),
    props.numberOfStudents
  );
  let pagination = [];

  if (numberOfPages > 1) {
    for (let i = 1; i < numberOfPages + 1; i++) {
      let pageNumber = (
        <span
          key={i}
          className={
            'pagination-number' + (i === props.currentPage ? ' active' : '')
          }
          data-page={i}
          onClick={(event) =>
            props.changePage(Number(event.target.dataset.page))
          }
        >
          {`${i}`}
        </span>
      );

      pagination.push(pageNumber);
    }
  }

  let addStudentButton = (
    <MenuButton
      buttonClass="button-secondary button-large padding-right-30 button"
      className="align-right button-container"
      icon={
        <ArrowDown key="menubutton-ArrowDown-1" className="add-student-icon" />
      }
      label={translate('Add Students')}
      menuItems={[
        {
          keyValue: 'add-one-student-item',
          label: translate('Add One Student'),
          onClick: () => {
            if (props.numberOfStudents === MAX_PRO_ROSTERED_STUDENTS) {
              toast(
                translate('You’ve reached your {0} student limit').format(
                  MAX_PRO_ROSTERED_STUDENTS
                ),
                { disableClose: true }
              );
            } else if (props.numberOfStudents >= maxStudentsInRoster.data) {
              paywallDispatch({
                type: 'open',
                payload: {
                  paywallTouch: [
                    PaywallScreens.RoomsRosterMaxStudent,
                    PaywallScreens.RoomsRosterAddOneStudent,
                  ],
                },
              });
            } else {
              c.addOneStudentClicked(props.roomId);
            }
          },
        },
        {
          keyValue: 'add-multiple-students-item',
          label: translate('Add Multiple Students'),
          onClick: () => {
            if (props.numberOfStudents === MAX_PRO_ROSTERED_STUDENTS) {
              toast(
                translate('You’ve reached your {0} student limit').format(
                  MAX_PRO_ROSTERED_STUDENTS
                ),
                { disableClose: true }
              );
            } else if (props.numberOfStudents >= maxStudentsInRoster.data) {
              paywallDispatch({
                type: 'open',
                payload: {
                  paywallTouch: [
                    PaywallScreens.RoomsRosterMaxStudent,
                    PaywallScreens.RoomsRosterAddManyStudents,
                  ],
                },
              });
            } else {
              c.manualStudentsClicked();
            }
          },
        },
      ]}
    />
  );

  if (platform.isMobile() && !platform.isTablet) {
    addStudentButton = (
      <button
        className="align-right button-secondary button-large button"
        onClick={() => {
          if (props.numberOfStudents === MAX_PRO_ROSTERED_STUDENTS) {
            toast(
              translate('You’ve reached your {0} student limit').format(
                MAX_PRO_ROSTERED_STUDENTS
              ),
              { disableClose: true }
            );
          } else if (props.numberOfStudents >= maxStudentsInRoster) {
            paywallDispatch({
              type: 'open',
              payload: { paywallTouch: PaywallScreens.RoomsRosterMaxStudent },
            });
          } else {
            c.addOneStudentClicked(props.roomId);
          }
        }}
      >
        {translate('Add')}
      </button>
    );
  }

  return (
    <div className="manage-roster-buttons clearfix">
      <div
        className="roster-action-buttons"
        style={{
          display:
            props.showMainButtons === false || props.hideRosterData
              ? 'none'
              : 'inline-block',
        }}
      >
        <div className="delete-list-btn button-container">
          <button
            className="button button-secondary-common button-large"
            type="button"
            onClick={() => {
              c.deleteRoster(roomName);
              queryClient.invalidateQueries(roomsKeys.list());
            }}
          >
            <span>{translate('Delete')}</span>
          </button>
        </div>
        <div className="download-list-btn button-container">
          <button
            className="button button-secondary-common button-large"
            type="button"
            onClick={() => c.exportClicked(roomName)}
          >
            <span>{translate('Export')}</span>
          </button>
        </div>
        {addStudentButton}
      </div>
      <div
        className="pagination-container"
        style={{ display: numberOfPages > 1 ? 'inline-block' : 'none' }}
      >
        <button
          className="button button-secondary-common previous-page"
          disabled={props.currentPage === 1}
          onClick={() => changePage(props.currentPage - 1)}
        >
          <i data-icon="A" />
        </button>
        {pagination}
        <button
          className="button button-secondary-common next-page"
          disabled={props.currentPage === numberOfPages}
          onClick={() => props.changePage(props.currentPage + 1)}
        >
          <i data-icon="a" />
        </button>
        <span className="students-on-page">
          {`(${firstStudentOrder}/${lastStudentOrder})`}
        </span>
      </div>
    </div>
  );
}

export default class RosterView extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      page: 1,
    };
  }

  cancelAddingNewStudents() {
    this.props.cancelAddingNewStudents(this.props.roomName);
  }

  render() {
    c = this.props.controller;
    m = this.props.model;

    let numberOfStudents = this.props.roster ? this.props.roster.length : 0;

    let topPagination = (
      <div>
        <RosterActionButtons
          numberOfStudents={numberOfStudents}
          changePage={(page) => this.setState({ page: page })}
          currentPage={this.state.page}
          roomName={this.props.roomName}
          hideRosterData={this.props.hideRosterData}
        />
      </div>
    );

    let bottomPagination = (
      <div>
        <RosterActionButtons
          showMainButtons={false}
          hideRosterData={this.props.hideRosterData}
          numberOfStudents={numberOfStudents}
          changePage={(page) => this.setState({ page: page })}
          currentPage={this.state.page}
          roomName={this.props.roomName}
        />
      </div>
    );

    let message = null;

    if (this.props.message) {
      message = <ImportRosterMessage name={this.props.roomName} />;
    }

    // add only one student in the roster
    // this will not be displayed in popup
    let newStudentHTML = null;

    if (this.props.newStudents && this.props.newStudents.length > 0) {
      newStudentHTML = (
        <StudentEntryView
          studentData={this.props.newStudents[0]}
          roomName={this.props.roomName}
          editMode={true}
          singular={true}
          {...this.props}
        />
      );
    }

    let studentsList = this.props.roster;

    if (studentsList && studentsList.length > 0) {
      let aux = (this.state.page - 1) * MAX_STUDENTS_ON_PAGE;
      studentsList = studentsList.slice(aux, aux + MAX_STUDENTS_ON_PAGE);
    }

    return (
      <div className="roster-container">
        {topPagination}
        {message}
        {newStudentHTML}
        <StudentsInRosterList
          roomName={this.props.roomName}
          roomId={this.props.roomId}
          logged_in={this.props.logged_in}
          studentsList={studentsList}
          students_logged_in={this.props.students_logged_in}
          hideRosterData={this.props.hideRosterData}
          {...this.props}
        />
        {bottomPagination}
      </div>
    );
  }
}
